<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  levels: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Course Levels'
  },
  description: {
    type: String,
    default: 'Select the education levels you teach'
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const toggleLevel = (id) => {
  const selected = [...props.modelValue]
  const index = selected.indexOf(id)

  if (index === -1) {
    selected.push(id)
  } else {
    selected.splice(index, 1)
  }

  emit('update:modelValue', selected)

  // Clear error when user makes a selection
  if (localErrors.value.selected_levels) {
    delete localErrors.value.selected_levels
  }
}

const isSelected = (id) => props.modelValue.includes(id)

const validate = () => {
  const errors = {}

  if (props.modelValue.length === 0) {
    errors.selected_levels = 'Please select at least one course level'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div class="bg-white rounded-2xl p-10">
    <div class="mb-8">
      <h2 class="text-lt md:text-2xl font-bold text-gray-900 mb-2">
        {{ title }} <span class="text-red-500">*</span>
      </h2>
      <p class="text-gray-600">{{ description }}</p>

      <!-- Error Message -->
      <div v-if="displayErrors.selected_levels" class="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">{{ displayErrors.selected_levels }}</p>
            <p class="text-red-700 text-sm mt-1">Choose at least one course level from the options below.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Simple View - Compact grid for settings -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
      <button
        v-for="level in levels"
        :key="level.id"
        type="button"
        @click="toggleLevel(level.id)"
        class="relative group px-4 py-3 rounded-lg border-2 transition-all hover:shadow-sm"
        :class="isSelected(level.id) ? '' : 'border-gray-200 bg-white hover:border-gray-300'"
        :style="{
          borderColor: isSelected(level.id) ? level.color : '',
          backgroundColor: isSelected(level.id) ? `${level.color}08` : ''
        }"
      >
        <span class="text-sm font-semibold text-gray-900 text-center block">{{ level.name }}</span>

        <!-- Triangle indicator for selected levels -->
        <div
          v-if="isSelected(level.id)"
          class="absolute top-0 right-0 w-0 h-0"
          :style="{
            borderTop: `20px solid ${level.color}`,
            borderLeft: '20px solid transparent'
          }"
        ></div>
      </button>
    </div>
  </div>
</template>
